import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CameraIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';

const UploadPhotos: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) { setError('Link inválido'); setLoading(false); return; }
    axios.get(`/api/workshop/monitor/photo-upload/${token}/info`)
      .then((res) => setInfo(res.data))
      .catch((err) => setError(err.response?.data?.error || 'Link inválido o expirado'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...arr]);
    arr.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => setPreviews((prev) => [...prev, e.target?.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    let count = 0;
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('photo', file);
        await axios.post(`/api/workshop/monitor/photo-upload/${token}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        count++;
      }
      setUploadedCount((c) => c + count);
      setSelectedFiles([]);
      setPreviews([]);
      toast.success(`${count} foto${count > 1 ? 's' : ''} subida${count > 1 ? 's' : ''} correctamente`);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al subir fotos');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-lg">Cargando...</div>;
  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-bold mb-2">Link no válido</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-4 flex flex-col">
      <div className="max-w-md mx-auto w-full space-y-4">
        {/* Info del vehículo */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="text-xs text-cyan-600 dark:text-cyan-400 font-mono">{info.appointmentCode}</div>
          <div className="text-2xl font-bold mt-1">{info.licensePlate || 'Sin patente'}</div>
          <div className="text-sm text-muted-foreground">{info.vehicle}</div>
          {uploadedCount > 0 && (
            <div className="flex items-center gap-1 mt-2 text-emerald-600 text-xs font-medium">
              <CheckCircleIcon className="h-4 w-4" />
              {uploadedCount} foto{uploadedCount > 1 ? 's' : ''} subida{uploadedCount > 1 ? 's' : ''} esta sesión
            </div>
          )}
        </div>

        {/* Selector de fotos */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl p-6 flex flex-col items-center gap-2 shadow-lg active:scale-95 transition-transform"
        >
          <CameraIcon className="h-12 w-12" />
          <span className="text-lg font-bold">Seleccionar fotos</span>
          <span className="text-xs opacity-80">Cámara o galería</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
        />

        {/* Preview de fotos seleccionadas */}
        {previews.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium">Fotos seleccionadas ({previews.length})</p>
            <div className="grid grid-cols-3 gap-2">
              {previews.map((src, i) => (
                <div key={i} className="relative aspect-square">
                  <img src={src} alt={`Foto ${i+1}`} className="w-full h-full object-cover rounded-lg border border-border" />
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-1 right-1 rounded-full bg-red-500 text-white p-0.5"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <Button
              onClick={uploadFiles}
              disabled={uploading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-lg py-6"
            >
              {uploading ? 'Subiendo...' : `Subir ${previews.length} foto${previews.length > 1 ? 's' : ''}`}
            </Button>
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Seleccioná las fotos y tocá Subir para enviarlas al sistema.
        </p>
      </div>
    </div>
  );
};

export default UploadPhotos;
